import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Upload,
  Loader2,
  Info,
  Layers,
} from 'lucide-react';

const SliceViewer = ({
  seriesDetail,
  sliceUrl,
  currentSlice,
  onSliceChange,
  uploading,
  onUploadSlice,
}) => {
  if (!seriesDetail) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-12 text-center">
          <Layers className="w-12 h-12 text-[#5C5F5C] mx-auto mb-4" />
          <p className="text-[#5C5F5C]">Select a series to view or create a new one</p>
          <p className="text-sm text-[#5C5F5C] mt-1">Use arrow keys for slice navigation</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E5E4E0] bg-white">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <div>
          <CardTitle className="text-base font-['Work_Sans']">{seriesDetail.series_name}</CardTitle>
          <p className="text-xs text-[#5C5F5C]">
            {seriesDetail.modality && `${seriesDetail.modality} · `}
            {seriesDetail.slice_count} slice{seriesDetail.slice_count !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".dcm,.dicom,.png,.jpg,.jpeg"
              multiple
              onChange={onUploadSlice}
              className="hidden"
              data-testid="upload-slice-input"
            />
            <Button
              variant="outline"
              size="sm"
              className="border-[#4B6651] text-[#4B6651]"
              disabled={uploading}
              asChild
            >
              <span>
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Add Slices
              </span>
            </Button>
          </label>
        </div>
      </CardHeader>
      <CardContent>
        {seriesDetail.slice_count > 0 ? (
          <div>
            <div className="bg-black rounded-lg overflow-hidden mb-4 flex items-center justify-center min-h-[400px]">
              {sliceUrl ? (
                <img
                  src={sliceUrl}
                  alt={`Slice ${currentSlice + 1}`}
                  className="max-w-full max-h-[500px] object-contain"
                  data-testid="slice-image"
                />
              ) : (
                <Loader2 className="w-8 h-8 animate-spin text-white" />
              )}
            </div>

            <div className="flex items-center justify-center gap-4" data-testid="slice-navigation">
              <Button
                variant="outline" size="sm"
                onClick={() => onSliceChange(currentSlice - 1)}
                disabled={currentSlice === 0}
                className="border-[#E5E4E0]"
                data-testid="prev-slice-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#5C5F5C]">
                  Slice {currentSlice + 1} of {seriesDetail.slice_count}
                </span>
                <input
                  type="range"
                  min={0}
                  max={seriesDetail.slice_count - 1}
                  value={currentSlice}
                  onChange={(e) => onSliceChange(parseInt(e.target.value))}
                  className="w-40 accent-[#4B6651]"
                  data-testid="slice-slider"
                />
              </div>
              <Button
                variant="outline" size="sm"
                onClick={() => onSliceChange(currentSlice + 1)}
                disabled={currentSlice >= seriesDetail.slice_count - 1}
                className="border-[#E5E4E0]"
                data-testid="next-slice-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {seriesDetail.slices?.[currentSlice]?.dicom_metadata && (
              <div className="mt-3 p-3 bg-[#F8F7F4] rounded-lg">
                <p className="text-xs font-semibold text-[#5C5F5C] uppercase mb-2 flex items-center gap-1">
                  <Info className="w-3 h-3" /> DICOM Metadata
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(seriesDetail.slices[currentSlice].dicom_metadata).map(([key, val]) => (
                    val && (
                      <div key={key}>
                        <span className="text-[#5C5F5C]">{key.replace(/_/g, ' ')}: </span>
                        <span className="text-[#2A2C2A] font-medium">{val}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <Layers className="w-10 h-10 text-[#5C5F5C] mx-auto mb-3" />
            <p className="text-[#5C5F5C]">No slices uploaded yet</p>
            <p className="text-sm text-[#5C5F5C] mt-1">Upload DICOM files to view the series</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SliceViewer;
